import React, { type ReactNode, useMemo } from 'react';

import AvatarGroup, { type AvatarGroupProps, type AvatarProps } from '@atlaskit/avatar-group';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { useThemeObserver } from '@atlaskit/tokens';

import usedAvatarDarkSrc from './assets/user-brand-dark.svg';
import usedAvatarLightSrc from './assets/user-brand-light.svg';
import dividerSrc from './assets/user-divider.svg';
import unusedAvatarDarkSrc from './assets/user-gray-dark.svg';
import unusedAvatarLightSrc from './assets/user-gray-light.svg';

const EMPTY_AVATAR: AvatarProps = {
	key: '',
	name: '',
};

type UserLimitsProps = {
	limit?: number;
	assignedUsers: AvatarProps[] | number;
	title: string;
	size?: AvatarGroupProps['size'];
	description: ReactNode;
	dividerLimit?: number;
};

const dividerStyles = xcss({
	marginRight: 'space.100',
	marginLeft: 'space.050',
	height: 'space.500',
});

export const UserLimits = ({
	title,
	description,
	assignedUsers,
	limit,
	size,
	dividerLimit,
}: UserLimitsProps) => {
	const { colorMode } = useThemeObserver();

	const avatars = useMemo(() => {
		const usedAvatarSrc = colorMode === 'dark' ? usedAvatarDarkSrc : usedAvatarLightSrc;
		const unusedAvatarSrc = colorMode === 'dark' ? unusedAvatarDarkSrc : unusedAvatarLightSrc;
		const usedAvatar = { ...EMPTY_AVATAR, src: usedAvatarSrc };
		const unusedAvatar = { ...EMPTY_AVATAR, src: unusedAvatarSrc };

		if (!Array.isArray(assignedUsers)) {
			const usedAvatars = new Array(assignedUsers).fill(usedAvatar);
			const unusedAvatars =
				limit && limit - assignedUsers > 0
					? new Array(limit - assignedUsers).fill(unusedAvatar)
					: [];
			return [...usedAvatars, ...unusedAvatars] as AvatarProps[];
		}

		if (limit === undefined || assignedUsers.length >= limit) {
			// No limit, just return the assigned users
			return assignedUsers;
		}

		const dummyAvatars = new Array(limit - assignedUsers.length).fill(unusedAvatar);

		return [...assignedUsers, ...dummyAvatars] as AvatarProps[];
	}, [assignedUsers, limit, colorMode]);

	return (
		<Stack space="space.250">
			<Stack>
				<Heading size="small">{title}</Heading>
				<Text size="small" color="color.text.subtlest">
					{description}
				</Text>
			</Stack>
			<Inline alignBlock="center">
				{dividerLimit !== undefined ? (
					<AvatarGroup
						appearance="stack"
						data={avatars}
						maxCount={dividerLimit + 1}
						size={size ?? 'medium'}
						overrides={{
							Avatar: {
								render: (Component, props, index) =>
									index === dividerLimit - 1 && avatars.length > dividerLimit ? (
										<Inline>
											<Component {...props} key={index} />
											<Box xcss={dividerStyles}>
												<Image src={dividerSrc} alt="Simple example" testId="image" />
											</Box>
										</Inline>
									) : (
										<Component {...props} key={index} />
									),
							},
						}}
					/>
				) : (
					<AvatarGroup appearance="stack" data={avatars} maxCount={11} size={size ?? 'medium'} />
				)}
			</Inline>
		</Stack>
	);
};
